import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface ConfirmOptions {
  title?: string;
  description?: ReactNode;
  details?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm?: () => Promise<void>;
}

type ConfirmFn = (opts: ConfirmOptions | string) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within <ConfirmProvider>.");
  return ctx;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions>({});
  const [busy, setBusy] = useState(false);
  const resolver = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((o) => {
    setOpts(typeof o === "string" ? { description: o } : o);
    setBusy(false);
    setOpen(true);
    return new Promise<boolean>((resolve) => { resolver.current = resolve; });
  }, []);

  function settle(result: boolean) {
    setOpen(false);
    setBusy(false);
    resolver.current?.(result);
    resolver.current = null;
  }

  async function onConfirmClick() {
    if (busy) return;
    if (!opts.onConfirm) {
      settle(true);
      return;
    }
    setBusy(true);
    try {
      await opts.onConfirm();
    } finally {
      settle(true);
    }
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={open} onOpenChange={(next) => { if (!next && !busy) settle(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{opts.title ?? "Are you sure?"}</DialogTitle>
            {opts.description != null && (
              <DialogDescription className="whitespace-pre-line">{opts.description}</DialogDescription>
            )}
          </DialogHeader>
          {opts.details}
          <DialogFooter>
            <Button
              autoFocus
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => settle(false)}
            >
              {opts.cancelLabel ?? "Cancel"}
            </Button>
            <Button
              type="button"
              variant={opts.destructive ? "destructive" : "default"}
              disabled={busy}
              onClick={onConfirmClick}
            >
              {busy && <Spinner />}
              {opts.confirmLabel ?? "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
